import { API_URL } from "config/environments"
import { isUndefined, random } from "lodash"

import { encryptionHelper } from "helpers/encryption"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { ZipWriter, BlobWriter, BlobReader } from "@zip.js/zip.js"

export enum ACTION_DOWNLOAD_WORKER {
  START = "DOWNLOAD_WORKER_START",
  PROCESS = "DOWNLOAD_WORKER_PROCESS",
  FINISH = "DOWNLOAD_WORKER_FINISH",
}

interface DataRequestDownloadOneFile {
  assetPath: string
  idConversationParam?: string
  project_build_id?: string
  projectId?: string
  project_component_history_id?: string
  decryptedKey?: string
  vaultKey?: string
  dbKey?: string
}
interface DownloadQueueItem {
  apiURL: string
  name: string
  subName: string
  downloadId: string
  buildId?: string
  done?: boolean
  errorMessage?: string
  isFileOne?: boolean
  dataFileOne?: DataRequestDownloadOneFile
  dbKey?: string
}

/* eslint-disable no-restricted-globals */
self.onmessage = async (event: MessageEvent) => {
  if (event.data.action !== ACTION_DOWNLOAD_WORKER.START) return
  const { itemQueue, tokenAuth, multipleFiles, fileFolderName } = event.data
    .payload as {
    itemQueue: DownloadQueueItem
    tokenAuth: string
    multipleFiles: any
    fileFolderName?: string
  }

  try {
    if (itemQueue.isFileOne && !isUndefined(itemQueue.dataFileOne)) {
      await handleDownloadOneFile(
        itemQueue.dataFileOne,
        itemQueue.downloadId,
        tokenAuth
      )
    } else {
      await handleDownloadMultipleFile(
        itemQueue.buildId || "",
        multipleFiles,
        itemQueue.downloadId,
        tokenAuth,
        itemQueue.apiURL,
        fileFolderName || "",
        itemQueue.dbKey
      )
    }
  } catch (err: any) {
    self.postMessage({
      action: ACTION_DOWNLOAD_WORKER.FINISH,
      payload: {
        dataBlob: null,
        messageError: "An unexpected error occurred. Please try again.",
        downloadId: itemQueue.downloadId,
        workerError: {
          phase: "INIT",
          message: err?.message || "Unknown worker error",
        },
      },
    })
  }
}

const decryptFileByGCM = async (
  fileResponse: Response,
  downloadId: string,
  key: string,
  hash_key?: string,
  public_key?: string,
  isPostMessage: boolean = true
) => {
  console.log("------- DECRYPT FILE-----------")
  if (!fileResponse.body) {
    return {
      dataBlob: null,
      messageError: "Unable to download: file is missing on the server.",
    }
  }
  const decryptKey = await encryptionGCMHelper.decryptKeyByCrypto(key)
  const decryptedStream = encryptionGCMHelper.decryptGCMStream(
    fileResponse.body,
    decryptKey
  )

  const dataBlob = await new Response(decryptedStream).blob()
  if (dataBlob.size === 0) {
    return {
      dataBlob: null,
      messageError: "Download failed: the file has no content.",
    }
  }
  console.log(444)
  if (isPostMessage) {
    self.postMessage({
      action: ACTION_DOWNLOAD_WORKER.PROCESS,
      payload: {
        message: "Verify signature...",
        downloadId,
        process: random(70, 80),
      },
    })
  }
  if (!hash_key || !public_key) {
    return {
      dataBlob,
    }
  }

  const publicCryptoKey = await encryptionGCMHelper.coverPublicKeyToCryptoKey(
    public_key
  )
  let verified = true
  if (hash_key.length === 64) {
    // verify file to server email
    verified = await encryptionGCMHelper.verifyBlobHash(dataBlob, hash_key)
  } else {
    const hashBuffer = await encryptionGCMHelper.calculateSHA256(dataBlob)
    const signatureBuffer = encryptionGCMHelper.base64ToArrayBuffer(hash_key)
    if (!publicCryptoKey) {
      return {
        dataBlob: null,
        messageError:
          "Download failed: unable to verify file integrity. Please try again.",
      }
    }
    verified = await crypto.subtle.verify(
      { name: "RSA-PSS", saltLength: 32 },
      publicCryptoKey,
      signatureBuffer,
      hashBuffer
    )
  }

  console.log("Signature", verified ? "//SUCCESS//" : "//FAILED//")
  if (verified) {
    return {
      dataBlob,
    }
  }
  return {
    dataBlob: null,
    messageError:
      "Download failed: file integrity check did not pass. The file may be corrupted.",
    workerError: {
      phase: "VERIFY_SIGNATURE",
      message: "Hash/signature mismatch (GCM)",
    },
  }
}

const decryptFileByCBC = async (
  fileResponse: Response,
  assetPath: string,
  key: string,
  downloadId: string,
  hash_key?: string,
  public_key?: string,
  isPostMessage: boolean = true
) => {
  const isSpecificationFileHtml =
    assetPath.includes("pcb/gerber/specification") &&
    assetPath.endsWith(".html")
  let blob: any = await fileResponse.blob()
  let temp = await blob.text()
  blob = null // Free memory
  const data = await encryptionGCMHelper.decryptContext(key, temp, true)
  temp = null // Free memory
  let newBlob: any = new Blob([data])
  if (newBlob.size === 0) {
    return {
      dataBlob: null,
      messageError: "Download failed: the file has no content.",
    }
  }
  if (isPostMessage) {
    self.postMessage({
      action: ACTION_DOWNLOAD_WORKER.PROCESS,
      payload: {
        message: "Verify signature...",
        downloadId,
        process: random(70, 80),
      },
    })
  }

  let fileText: any = await newBlob.text()
  if (hash_key && public_key) {
    const verified = encryptionHelper.checkVerifiedSignature(
      public_key,
      hash_key,
      fileText
    )
    fileText = null // Free memory
    if (verified) {
      return {
        dataBlob: newBlob,
      }
    } else {
      if (!isSpecificationFileHtml) {
        return {
          dataBlob: null,
          messageError:
            "Download failed: file integrity check did not pass. The file may be corrupted.",
        }
      }
    }
  }

  return {
    dataBlob: newBlob,
  }
}

const fetchFileAndSignature = async (
  assetPath: string,
  key: string,
  tokenAuth: string,
  downloadId: string,
  extraParams?: Record<string, any>,
  isPostMessage: boolean = true,
  dbKey?: string
) => {
  try {
    console.log("-------START DECRYPT FILE-----------")
    const fileURL = encryptionGCMHelper.getFullAssetURI(assetPath, extraParams)

    const [fileResponse, hashKeyResponse] = await Promise.all([
      fetch(fileURL, {
        headers: {
          Authorization: tokenAuth,
          ...(dbKey ? { "x-db-key": dbKey } : {}),
        },
      }),
      fetch(
        `${API_URL}/api/file/hash?file_key=${encodeURIComponent(assetPath)}`,
        {
          headers: {
            Authorization: tokenAuth,
            "Content-Type": "application/json",
            ...(dbKey ? { "x-db-key": dbKey } : {}),
          },
        }
      ),
    ])

    if (!fileResponse.ok) {
      const userMsg =
        fileResponse.status === 404
          ? "Unable to download: file no longer exists on the server."
          : fileResponse.status === 403
          ? "Unable to download: the download link has expired. Please refresh and try again."
          : fileResponse.status === 401
          ? "Unable to download: your session has expired. Please log in again."
          : `Unable to download: server returned an error (${fileResponse.status}).`

      return {
        dataBlob: null,
        messageError: userMsg,
        workerError: {
          phase: "FETCH_FILE",
          message: `HTTP ${fileResponse.status}`,
          file_url: fileURL,
          http_status: fileResponse.status,
        },
      }
    }

    if (!hashKeyResponse.ok) {
      return {
        dataBlob: null,
        messageError: "Unable to verify file. Please try downloading again.",
        workerError: {
          phase: "FETCH_HASH",
          message: `HTTP ${hashKeyResponse.status}`,
          http_status: hashKeyResponse.status,
        },
      }
    }

    const metadataResponse = fileResponse.headers.get("x-meta-data")
    const { hash_key, public_key } = await hashKeyResponse.json()
    const metadataJson: {
      "mime-type": string
      "encrypt-version": string
    } = metadataResponse ? JSON.parse(metadataResponse) : {}
    if (isPostMessage) {
      self.postMessage({
        action: ACTION_DOWNLOAD_WORKER.PROCESS,
        payload: {
          message: "Decrypting file...",
          downloadId,
          process: random(15, 30),
        },
      })
    }
    if (metadataJson["encrypt-version"]) {
      return await decryptFileByGCM(
        fileResponse.clone(),
        downloadId,
        key,
        hash_key,
        public_key,
        isPostMessage
      )
    }
    try {
      const gcmResult = await decryptFileByGCM(
        fileResponse.clone(),
        downloadId,
        key,
        hash_key,
        public_key,
        false
      )

      if (!gcmResult.messageError) {
        return gcmResult
      }
    } catch (e: any) {
      console.log("Decrypt failed", e?.message)
    }

    return await decryptFileByCBC(
      fileResponse,
      assetPath,
      key,
      downloadId,
      hash_key,
      public_key,
      isPostMessage
    )
  } catch (err: any) {
    return {
      dataBlob: null,
      messageError:
        "Download failed: an error occurred while verifying the file. Please try again.",
      workerError: {
        phase: "VERIFY_SIGNATURE",
        message: err?.message || "Unknown verification error",
      },
    }
  } finally {
    console.log("-------END DECRYPT FILE-----------")
  }
}

const handleDownloadOneFile = async (
  dataRequest: DataRequestDownloadOneFile,
  downloadId: string,
  tokenAuth: string
) => {
  const {
    assetPath,
    idConversationParam,
    project_build_id,
    project_component_history_id,
  } = dataRequest

  const params: Record<string, string> = {}
  if (project_build_id) params["project_build_id"] = project_build_id
  if (project_component_history_id && !idConversationParam)
    params["project_component_history_id"] = project_component_history_id
  if (idConversationParam) params["conversation_id"] = idConversationParam

  const fetchResult = await fetchFileAndSignature(
    assetPath,
    dataRequest.decryptedKey || "",
    tokenAuth,
    downloadId,
    params,
    undefined,
    dataRequest.dbKey
  )
  const { dataBlob, messageError } = fetchResult
  const workerError =
    "workerError" in fetchResult ? fetchResult.workerError : undefined

  self.postMessage({
    action: ACTION_DOWNLOAD_WORKER.FINISH,
    payload: {
      dataBlob,
      messageError,
      downloadId,
      workerError: workerError ?? null,
    },
  })
}

const handleDownloadMultipleFile = async (
  buildId: string,
  multipleFiles: any[],
  downloadId: string,
  tokenAuth: string,
  apiURL: string,
  fileFolderName: string,
  dbKey?: string
) => {
  console.log("-------START DOWNLOAD MULTIPLE FILES-----------")
  const conversationId = getParameterByName("conversation_id", apiURL)
  const zipWriter = new ZipWriter(new BlobWriter("application/zip"))
  const existingPaths = new Set<string>()
  const process = 75 / multipleFiles.length
  let currentProgress = 15
  const skippedFiles: string[] = []
  for (let i = 0; i < multipleFiles.length; i++) {
    const file = multipleFiles[i]
    const newProcess = (currentProgress += process)
    self.postMessage({
      action: ACTION_DOWNLOAD_WORKER.PROCESS,
      payload: {
        message: `Decrypting and verifying signature:${i + 1}/${
          multipleFiles.length
        }`,
        downloadId,
        process: newProcess,
      },
    })
    if (file.buffer) {
      let fileContent = file.buffer
      if (file.messages && file.messages.length > 0) {
        for (const message of file.messages) {
          const rawMessage = (await encryptionGCMHelper.decryptContext(
            message.messageKey,
            message.message
          )) as string
          fileContent = fileContent.replace(message.uniqueKey, rawMessage)
        }
      }
      if (
        (file.file.split("/").slice(-1)[0] as string).search("txt") !== -1 ||
        (file.file.split("/").slice(-1)[0] as string).search("csv") !== -1
      ) {
        const blob = new Blob([fileContent])
        const fileNameBuffer = file.file.split("/").slice(-1)[0]
        await zipWriter.add(
          `${file.keyToFolder}/${removeTimestampInFileName(fileNameBuffer)}`,
          new BlobReader(blob)
        )
      } else {
        const { dataBlob, messageError } = await fetchFileAndSignature(
          file.file,
          file.key,
          tokenAuth,
          downloadId,
          {},
          false, // Disable post message for this fetch
          dbKey
        )
        if (!messageError && file.keyToFolder) {
          const fileName = file.file.split("/").slice(-1)[0]
          const uniqueName = getUniqueFileName(
            existingPaths,
            file.keyToFolder,
            removeTimestampInFileName(fileName)
          )
          await zipWriter.add(uniqueName, new BlobReader(dataBlob))
        }
      }
    } else if (file.file && !file.buffer && file.buffer !== "") {
      try {
        const isSpecificationFileHtml =
          file.file.includes("pcb/gerber/specification") &&
          file.file.endsWith(".html")
        const params: Record<string, string> = {}
        const isAdditionalInfo =
          file.keyToFolder === "additional_info" && buildId
        if (isAdditionalInfo) params["project_build_id"] = buildId
        if (conversationId) params["conversation_id"] = conversationId
        const { dataBlob, messageError } = await fetchFileAndSignature(
          file.file,
          file.key,
          tokenAuth,
          downloadId,
          params,
          false, // Disable post message for this fetch
          dbKey
        )
        if (messageError && isSpecificationFileHtml) {
          continue
        } else if (messageError) {
          skippedFiles.push(file.file ?? `index-${i}`)
          continue
        }
        const fileName = file.file.split("/").slice(-1)[0]

        const uniqueName = getUniqueFileName(
          existingPaths,
          file.keyToFolder,
          removeTimestampInFileName(fileName)
        )
        await zipWriter.add(uniqueName, new BlobReader(dataBlob))
      } catch (error: any) {
        console.error("Error fetching file:", error)
        skippedFiles.push(file.file ?? `index-${i}`)
        continue
      }
    }
  }
  self.postMessage({
    action: ACTION_DOWNLOAD_WORKER.PROCESS,
    payload: {
      message: "Zipping file...",
      downloadId,
      process: 97, // 97% after processing files
    },
  })
  await delay(2000) // Delay to ensure the last message is processed
  const blobZip = await zipWriter.close()

  self.postMessage({
    action: ACTION_DOWNLOAD_WORKER.FINISH,
    payload: {
      dataBlob: blobZip,
      messageError: "",
      downloadId,
      fileFolderName,
      workerError:
        skippedFiles.length > 0
          ? {
              phase: "ZIP",
              message: `${skippedFiles.length} file(s) skipped due to errors`,
              skipped_files: skippedFiles,
            }
          : null,
    },
  })
  console.log("-------END DOWNLOAD MULTIPLE FILES-----------")
}

const getParameterByName = (name: string, url: string) => {
  name = name.replace(/[\[\]]/g, "\\$&")
  var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
    results = regex.exec(url)
  if (!results) return undefined
  if (!results[2]) return undefined
  return decodeURIComponent(results[2].replace(/\+/g, " "))
}

const removeTimestampInFileName = (fileName: string) => {
  return fileName.replace(/_(\d+)(?=\.[^.]+$)/, "")
}

export function getUniqueFileName(
  existingPaths: Set<string>,
  folderPath: string | null,
  fileName: string
): string {
  const baseName = fileName.replace(/(\.[^.]*)$/, "")
  const extensionMatch = fileName.match(/(\.[^.]*)$/)
  const extension = extensionMatch ? extensionMatch[1] : ""

  const folderPrefix = folderPath ? `${folderPath}/` : ""
  let newFilePath = `${folderPrefix}${fileName}`
  let counter = 1

  while (existingPaths.has(newFilePath)) {
    newFilePath = `${folderPrefix}${baseName} (${counter})${extension}`
    counter++
  }

  existingPaths.add(newFilePath)
  return newFilePath
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
